/**
 * ONE's push lifecycle manager.
 *
 * Mounted inside the AUTHENTICATED tree (App.tsx, within
 * `NotificationsDrawerProvider`), so registration — and the browser's
 * notification-permission prompt it may surface — happens as a deliberate
 * post-login moment, never for anonymous visitors (mirrors AIM's
 * UserDataProvider trigger, minus the Beacon/kiosk gates that are AIM-only
 * surfaces).
 *
 * Lifecycle plumbing (register-on-login + 7-day cadence, SW message
 * subscription) comes from `@aegix/web-push`. This component
 * owns ONE's behavior:
 *  - foreground `token_refresh` handling (SDK `onMessage`);
 *  - the service-worker relay: silent `TOKEN_REFRESH` re-registration and
 *    `ONE_PUSH_MESSAGE` (a ONE-category push delivered while a tab is
 *    focused → in-app toast + notifications drawer instead of a system
 *    notification) and `ONE_PUSH_CLICK` (a system-notification click
 *    relayed by the SW → deep-route to the event detail via
 *    `resolveOnePushClickRoute`; the SW already focused this window, so a
 *    payload with no concrete target navigates nowhere). Identity for the
 *    relay path comes from the PAGE side (auth-identity user +
 *    `getOneAccessToken()`, sessionStorage) — the SW never decodes
 *    tokens.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, type AllauthUser } from '@aegix/auth-identity';
import {
  SW_MESSAGE_TOKEN_REFRESH,
  useServiceWorkerMessages,
  useWebPushRegistrationSchedule,
} from '@aegix/web-push';
import { toaster } from '@/components/ui/toaster';
import { useNotificationsDrawer } from '@/contexts/NotificationsDrawerContext';
import { getOneAccessToken } from '@/lib/one-auth';
import { registerForPush } from './registration';
import { subscribeForegroundMessages } from './firebase';
import { ONE_PUSH_CATEGORIES, resolveOnePushClickRoute } from './clickRouting';
import { PushPrimingBanner } from './PushPrimingBanner';
import {
  canRegisterWithoutPriming,
  rememberPrimingDismissed,
  shouldShowPriming,
} from './pushPriming';

// The category set lives in clickRouting.ts (shared with the click
// mapping); re-exported here for import sites that reference this module.
export { ONE_PUSH_CATEGORIES } from './clickRouting';

interface OnePushData {
  type?: string;
  title?: string;
  body?: string;
  [key: string]: unknown;
}

export function PushNotificationsManager() {
  const user = useUser<AllauthUser>();
  const { addLocalNotification } = useNotificationsDrawer();
  const navigate = useNavigate();

  // Stable refs so long-lived listeners see current values without
  // re-subscribing on every render (App.tsx recreates the drawer callbacks).
  const userIdRef = useRef(user?.id);
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);
  const addLocalNotificationRef = useRef(addLocalNotification);
  useEffect(() => {
    addLocalNotificationRef.current = addLocalNotification;
  }, [addLocalNotification]);
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Hold registration while the browser permission is still
  // `default`, so `getToken()` cannot surface a COLD native prompt. The
  // explainer below owns that moment; accepting it flips `primed`, which
  // re-enables the schedule and lets the native prompt appear informed.
  // `granted`/`denied`/no-API all pass straight through.
  const [primed, setPrimed] = useState(() => canRegisterWithoutPriming());
  const [showPriming, setShowPriming] = useState(
    () => !canRegisterWithoutPriming() && shouldShowPriming(),
  );

  const handleEnable = useCallback(() => {
    setShowPriming(false);
    setPrimed(true); // schedule re-enables -> registerForPush -> native prompt
  }, []);

  const handleDismiss = useCallback(() => {
    setShowPriming(false);
    rememberPrimingDismissed();
  }, []);

  // Register on login; re-register every 7 days to keep the token fresh and
  // catch rotation (matches AIM's cadence).
  useWebPushRegistrationSchedule({
    userId: user?.id,
    enabled: primed,
    register: registerForPush,
  });

  // Foreground silent pushes via the SDK (token_refresh only — category
  // display for a focused tab arrives through the SW relay below).
  useEffect(() => {
    if (!user?.id) return;
    const unsubscribe = subscribeForegroundMessages((payload) => {
      if (payload?.data?.['type'] === 'token_refresh' && userIdRef.current) {
        void registerForPush();
      }
    });
    return unsubscribe;
  }, [user?.id]);

  // Service-worker relay messages.
  const handleSwMessage = useCallback((event: MessageEvent) => {
    const { type } = (event.data ?? {}) as { type?: string };

    if (type === SW_MESSAGE_TOKEN_REFRESH) {
      // Page-side identity gate: only re-register while a user is signed
      // in on THIS page (sessionStorage token) — the SW itself has
      // no auth context.
      if (userIdRef.current && getOneAccessToken()) {
        void registerForPush();
      }
      return;
    }

    if (type === 'ONE_PUSH_CLICK') {
      // System-notification click, relayed because this window was
      // already open (warm path). The SW has focused this window; only
      // navigate when the payload resolves to a concrete deep target —
      // otherwise leave the user where they are.
      const data = ((event.data as { data?: OnePushData })?.data ??
        null) as OnePushData | null;
      const route = resolveOnePushClickRoute(data);
      if (route) navigateRef.current(route);
      return;
    }

    if (type === 'ONE_PUSH_MESSAGE') {
      const data = ((event.data as { data?: OnePushData })?.data ??
        {}) as OnePushData;
      if (!data.type || !ONE_PUSH_CATEGORIES.has(data.type)) return;
      toaster.create({
        title: data.title ?? '',
        description: data.body ?? '',
        type: 'info',
        closable: true,
      });
      addLocalNotificationRef.current({
        title: data.title ?? '',
        body: data.body ?? '',
        category: data.type,
        priority: 'normal',
        action_url: null,
      });
    }
  }, []);
  useServiceWorkerMessages({
    enabled: user?.id != null,
    onMessage: handleSwMessage,
  });

  if (showPriming && user?.id != null) {
    return (
      <PushPrimingBanner onEnable={handleEnable} onDismiss={handleDismiss} />
    );
  }

  return null;
}
